/**
 * The account on this device (part 36a, tables AC and SE, principles C1–C8): the controller the interface drives. It
 * reads the record the device keeps, checks the kept trial copy against the relay key the build ships, and says what
 * the account allows (`permit`: whether the writer may change their books). It signs up, completes a verify link, logs
 * in and out, and keeps a session in the page's memory only, refreshed by the device key; every new copy goes through
 * C2 before it is kept. Another tab's change of the record is read at once (CL14). It never touches the book store (C1),
 * but says which one the page shows (`shelf`, deep-fix r4, table BK: books belong to the account).
 * From part 36c it keeps the account's view (its address, second factor, deletion and terms), lends its session to the
 * account page (`get`, `token`), keeps each pending deletion as this device saw it, and once a deletion is final (table
 * FD) queues that account's books on the device for deletion, which the next start carries out before any book store
 * opens (C14). Every effect is injected, so it runs against the relay in the same process in tests.
 */
import { batch, createSignal } from 'solid-js';
import { createDeviceSigningKey } from '@cockatrice/crypto';
import type { KdfParameters } from '@cockatrice/crypto';
import type { AccountConfig } from './config.ts';
import type { RelayClient } from './client.ts';
import { logIn, logOutDevice, refreshSession, secondStep, signUp, verifyLink } from './flows.ts';
import type { FlowDeps, Problem, SecondProof, Typed, Versions } from './flows.ts';
import type { DeviceKey } from './device.ts';
import type { Email } from './email.ts';
import { NO_RECORD, owing, settled } from './record.ts';
import type { AccountRecord, StoredAccount } from './record.ts';
import { sameShelf, shelfOf } from './shelf.ts';
import type { Claim, Shelf } from './shelf.ts';
import { mayWrite, trialStanding } from './status.ts';
import type { AccountStatus, TrialStanding } from './status.ts';
import { anchoredAt, importRelayKey, judgedAt, newerCopy, newestOf, verifyCopy } from './trial.ts';
import type { Anchor, KeptCopy } from './trial.ts';
import type { AccountStore } from './store.ts';
import type { KdfAnswer, KdfJob } from '../kdf/protocol.ts';
import { isAccountId as isAccountIdOf, readAccountView } from './wire.ts';
import type { AccountId, AccountView, IssuedSession, LoggedIn, PendingDeletion, SecondFactorNeeded, SignedCopy } from './wire.ts';
import type { Reply } from './client.ts';
import { canonicalEmail } from './email.ts';
import type { DeletionMark } from './record.ts';
import type { DoomedNames } from './store.ts';
import { DAY_MS } from './trial.ts';

/** A channel between the app's tabs (a `BroadcastChannel`), as far as the account uses it. */
export interface AccountChannel {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: { readonly data: unknown }) => void): void;
}

/** The message a tab sends when it changed the record (CL14). */
const CHANGED = 'account-changed';

/** How often the time C2 judges at moves on while the page is open, so writing stops at the trial's end. */
export const TICK_MS = 60_000;

/** SE1: a session with less than this left is refreshed before a request. */
export const SESSION_MARGIN_MS = 60_000;

/** SE4: the shortest wait between two refreshes that failed for the network or the relay's load. */
export const REFRESH_PAUSE_MS = 60_000;

/**
 * Fix-next 84, FM9: how long past a deletion's due date a device that logged itself out keeps its mark on `logged-out`
 * (the relay's deletion purge runs in its hourly sweep, so a week is ample); after it, the deletion was cancelled.
 */
export const LOGGED_OUT_GRACE_MS = 7 * DAY_MS;

/** What the account needs when the build names a relay (AC1): its settings, the relay, WebCrypto and the KDF worker. */
export interface AccountSetup {
  readonly config: AccountConfig;
  readonly client: RelayClient;
  readonly subtle: SubtleCrypto;
  readonly derive: (job: KdfJob) => Promise<KdfAnswer>;
}

/** What the controller runs on. */
export interface AccountOptions {
  /** The relay and what reaching it takes, or null: accounts off (AC1, C6). */
  readonly setup: AccountSetup | null;
  /** Opens the account's storage (`openAccountStore`). */
  readonly openStore: () => Promise<AccountStore>;
  readonly random: (bytes: Uint8Array<ArrayBuffer>) => Uint8Array;
  readonly now: () => number;
  /** The page's monotonic clock (`performance.now`), which the wall clock's changes don't move (table TJ′). */
  readonly monotonic: () => number;
  /** Calls `tick` every `ms` while the page lives. */
  readonly every: (ms: number, tick: () => void) => void;
  readonly channel: () => AccountChannel;
  /**
   * Part 38c (GP3): told once a typed password admitted `account` (a login, its second step, a verify link), with what
   * the password gave, while it is at hand: the invitee's keyring opens and a waiting invite is claimed then. Its
   * failures are its own; the login stands.
   */
  readonly unlocked?: (event: Unlocked) => Promise<void>;
  /** Part 38c (GP4, CC10): told before a logout of `account` completes, so its shared books' cache goes with it. */
  readonly loggedOut?: (account: AccountId) => Promise<void>;
  /** Whether the browser says it is online (`navigator.onLine`); a false never stops a try, it only skips the start's. */
  readonly online: () => boolean;
  /** FD8: the names of a shelf's storage (`shell/space.ts`'s), for a deleted account's. */
  readonly namesOf: (shelf: Shelf) => DoomedNames;
  /** FD9: deletes a deleted account's storage: true once it is gone, false when it can't now (a page holds it open). */
  readonly deleteStores: (names: DoomedNames) => Promise<boolean>;
}

/** Part 38c (GP3): a typed password admitted `account` at `email`; `typed` holds what it gave. */
export interface Unlocked {
  readonly account: AccountId;
  readonly email: Email;
  readonly typed: Typed;
}

/** A step's outcome as the interface reads it: done, the second step to take, or the problem. */
export type StepOutcome = { readonly kind: 'done' } | { readonly kind: 'second-factor'; readonly need: SecondFactorNeeded } | Problem;

/** What the flows run on here, with the store their device key and highest parameters come from. */
type Deps = FlowDeps & { readonly store: AccountStore };

/**
 * FD1–FD4: `deleting` with `account`'s mark for `deletion` as told now: a new due date marks it seen at `now`, the same
 * one keeps its first sight, none takes the mark away.
 */
export function marked(deleting: Readonly<Record<string, DeletionMark>>, account: AccountId, deletion: Pick<PendingDeletion, 'dueAt'> | null, now: number): Readonly<Record<string, DeletionMark>> {
  const { [account]: kept, ...others } = deleting;
  if (deletion === null) return others;
  return { ...others, [account]: kept?.dueAt === deletion.dueAt ? kept : { dueAt: deletion.dueAt, seenAt: now } };
}

/** Whether an outcome of the relay ends this device's session (SE3). */
function endsSession(problem: Problem): boolean {
  return problem.kind === 'refused' && ['device-revoked', 'device-expired', 'login-required', 'logged-out', 'unauthenticated', 'account-deleted'].includes(problem.error);
}

/** The account controller. */
export class Account {
  private readonly options: AccountOptions;
  private readonly statusSignal = createSignal<AccountStatus>({ kind: 'loading' });
  private readonly clock: ReturnType<typeof createSignal<number>>;
  private store: AccountStore | null = null;
  /** The build's relay key, imported, with the WebCrypto that checks copies under it; null before start or a bad key. */
  private verifier: { readonly subtle: SubtleCrypto; readonly key: CryptoKey } | null = null;
  /** SE1: the session held, in the page's memory only (C3), and whose it is. */
  private session: { readonly account: AccountId; readonly issued: IssuedSession } | null = null;
  /** The newest copy that verified, kept with its signed form. */
  private kept: KeptCopy | null = null;
  private latestSeen = 0;
  /** Deep-fix r2, TJ1: the newest copy the relay sent on this page, for its account. */
  private anchor: (Anchor & { readonly account: AccountId }) | null = null;
  private lastFailedRefresh: number | null = null;
  /** Fix-next 84, LXC4: the owed logouts being sent, which a login waits for so it never lands before one. */
  private settling: Promise<void> | null = null;
  private channel: AccountChannel | null = null;
  /**
   * Deep-fix r1, table GT: the writer said "not now" on this page. Kept in memory as well as in the record, so a store
   * that can't open, read or write never keeps the gate over the books (C1: reading and export never wait on it).
   */
  private dismissed = false;
  /** A login's second step waiting: its ticket's address and the highest parameters to keep once it passes. */
  private pending: { readonly email: Email; readonly highest: KdfParameters; readonly typed: Typed } | null = null;
  /** BK, FC: the device's first store's claim, as last read; undefined for not known. */
  private claimed: Claim | undefined = undefined;
  /** BK: the shelf the account's state gives; null until the start has read it. */
  private readonly shelfSignal = createSignal<Shelf | null>(null);
  /** BK7: the shelf the page opened its books on, once it did; it stays for the page's life. */
  private readonly pageSignal = createSignal<Shelf | null>(null);
  /** Deep-fix r7, BF3: the page came back from the back-forward cache and hasn't read the record again yet. */
  private readonly resyncing = createSignal(false);
  /** BK7: pages waiting for the start to read the shelf. */
  private readonly waiting: ((shelf: Shelf) => void)[] = [];
  /** Part 36c: what `GET /v1/account` last said of the account logged in (its address, F, deletion, terms); null before. */
  private readonly viewSignal = createSignal<AccountView | null>(null);
  /** FD9: the book stores queued for deletion and not deleted yet: no page shows them (they are a deleted account's). */
  private doomedBooks: ReadonlySet<string> = new Set();
  /** FD7, FD10: when the device last asked the relay whether a deleted account is gone. */
  private lastGoneCheck: number | null = null;

  constructor(options: AccountOptions) {
    this.options = options;
    this.clock = createSignal(options.now());
    // BK6: accounts off: the first store, as before accounts.
    if (options.setup === null) {
      this.statusSignal[1]({ kind: 'off' });
      this.setShelf({ kind: 'first' });
    }
  }

  /** The account's state (table AC). */
  readonly status = (): AccountStatus => this.statusSignal[0]();

  /** C2: the time the copy is judged at now (moves on every {@link TICK_MS}). */
  readonly judgedAt = (): number => {
    const status = this.status();
    const now = this.clock[0]();
    // J1–J5: a copy came from the relay on this page: its time and the time since, never below the time kept (J4);
    // J6: otherwise C2's latest.
    if (status.kind === 'account' && this.anchor?.account === status.id) return Math.max(anchoredAt(this.anchor, now, this.options.monotonic()), this.latestSeen);
    return judgedAt(now, this.latestSeen, status.kind === 'account' ? status.copy : null);
  };

  /**
   * C1, C2: whether the writer may change their books now; BK8: never the books of a shelf the account has left, nor
   * the empty store a page shows when no shelf is the account's (BK2, BK9), whose books would be nobody's.
   */
  readonly permit = (): boolean => mayWrite(this.status(), this.judgedAt()) && !this.moved() && this.pageSignal[0]()?.kind !== 'none' && !this.resyncing[0]();

  /** BK: the shelf the account's state gives now; null until the start has read it. */
  readonly shelf = (): Shelf | null => this.shelfSignal[0]();

  /** BK8: whether the page's books are no longer the account's shelf (another account, or logged out): the page reloads. */
  readonly moved = (): boolean => {
    const page = this.pageSignal[0]();
    const shelf = this.shelfSignal[0]();
    return page !== null && shelf !== null && !sameShelf(page, shelf);
  };

  /**
   * BK7: the shelf the page opens its books on: the first time, the account's once the start has read it; after that,
   * the same one for the page's life (a reconnect reopens the same store, never another account's).
   */
  async openShelf(): Promise<Shelf> {
    const page = this.pageSignal[0]();
    if (page !== null) return page;
    // The shelf now, which may have changed since it was first known; of two asking at once, the first pins it.
    const shelf =
      this.shelfSignal[0]() ??
      (await new Promise<Shelf>((resolve) => {
        this.waiting.push(resolve);
      }));
    return this.pageSignal[0]() ?? this.pageSignal[1](shelf);
  }

  /** What the interface says of the trial now. */
  readonly standing = (): TrialStanding => trialStanding(this.status(), this.judgedAt());

  /** The website's origin, for the terms' and the privacy policy's links; empty with accounts off (no card asks then). */
  readonly site = (): string => this.options.setup?.config.site ?? '';

  /** Part 38d: the reader page's origin the build names, or null (reading links off). */
  readonly readerOrigin = (): string | null => this.options.setup?.config.reader ?? null;

  /** Part 38d: WebCrypto as the account was given it, or null with accounts off. */
  readonly subtle = (): SubtleCrypto | null => this.options.setup?.subtle ?? null;

  /**
   * AC1–AC7: reads the record and the kept copy, then, logged in and online, refreshes the session and the account's
   * view in the background. With accounts off it does nothing at all (C6).
   */
  async start(): Promise<void> {
    const setup = this.options.setup;
    if (setup === null) return;
    const key = await importRelayKey(setup.subtle, setup.config.trialKey);
    this.verifier = key === null ? null : { subtle: setup.subtle, key };
    this.options.every(TICK_MS, () => {
      this.clock[1](this.options.now());
    });
    let store: AccountStore;
    try {
      store = await this.options.openStore();
    } catch {
      // The store can't open (a private window that refuses IndexedDB): no account can be kept, so none is shown; the
      // gate still closes on "not now" (GT1). BK9: whose the first store is can't be read, so it shows, read-only.
      this.statusSignal[1]({ kind: 'none', gate: !this.dismissed });
      this.setShelf({ kind: 'first' });
      return;
    }
    this.store = store;
    // FD9: a deleted account's stores go before any book store opens; one a page still holds waits for the next start.
    await this.deleteDoomed(store);
    this.channel = this.options.channel();
    this.channel.addEventListener('message', (event) => {
      if (event.data === CHANGED) void this.reread(store);
    });
    await this.reread(store);
    await this.keepTime();
    const status = this.status();
    if (!this.options.online()) return;
    // LXC3: a logout owed since an earlier page, sent first.
    await this.settleOwed();
    if (status.kind === 'account' && !status.signedOut) await this.refresh();
    await this.checkGone();
  }

  /** The page is hidden: the latest time seen is kept (C2). */
  hidden(): void {
    void this.keepTime();
  }

  /**
   * The page is shown again: a refresh is due when the session has ended (SE1), so the copy is fresh. BF4: the record is
   * read again too (a missed message), without holding writing: the writer coming back types at once.
   */
  visible(): void {
    const status = this.status();
    this.clock[1](this.options.now());
    if (this.store !== null) void this.reread(this.store);
    if (status.kind === 'account' && !status.signedOut && !this.live()) void this.refresh();
    // LXC3: back on the page, maybe online again: a logout still owed goes.
    void this.settleOwed();
    void this.checkGone();
  }

  /**
   * Deep-fix r7, BF3: the page is back from the back-forward cache, where other tabs' messages don't reach it: nothing is
   * written until the record has been read again, so a logout or another account there stops this page first.
   */
  resumed(): void {
    const store = this.store;
    if (store === null) return;
    this.resyncing[1](true);
    void this.reread(store).then(() => {
      this.resyncing[1](false);
    });
  }

  /** AC3: "not now": no account on this device for now; the gate stays away until asked for. */
  async notNow(): Promise<void> {
    if (this.options.setup === null) return;
    // GT1, GT3: the gate closes now, whether or not the record takes the choice.
    this.dismissed = true;
    const status = this.status();
    if (status.kind === 'none') this.statusSignal[1]({ kind: 'none', gate: false });
    await this.change((record) => ({ ...record, notNow: true }));
  }

  /** AC2: the gate asked for again (from the banner or settings). */
  showGate(): void {
    const status = this.status();
    if (status.kind !== 'none') return;
    // GT5: asked for, the gate shows again until the next "not now".
    this.dismissed = false;
    this.statusSignal[1]({ kind: 'none', gate: true });
  }

  /** SU1: signs `email` up with `password`, accepting `accepted`; part 38c (JL1): as a guest when `guest`. */
  async signUp(email: Email, password: string, accepted: Versions, guest = false): Promise<{ readonly kind: 'check-email' } | Problem> {
    const deps = this.deps();
    return deps === null ? { kind: 'failed' } : signUp(deps, email, password, accepted, guest);
  }

  /** SU6, SU7: completes a verify link on this device; logged in when it does. */
  async verify(token: string, email: Email, password: string): Promise<{ readonly kind: 'done' } | { readonly kind: 'wrong-address' } | Problem> {
    const deps = this.deps();
    if (deps === null) return { kind: 'failed' };
    await this.settling;
    const outcome = await verifyLink(deps, token, email, password, await this.highest(deps.store, email));
    if (outcome.kind !== 'logged-in') return outcome;
    await this.admitted(email, outcome.answer, outcome.highest);
    await this.unlock({ account: outcome.answer.account, email, typed: outcome.typed });
    return { kind: 'done' };
  }

  /** LG1–LG12: logs `email` in; the second step is `secondStep`'s. */
  async logIn(email: Email, password: string): Promise<StepOutcome> {
    const deps = this.deps();
    if (deps === null) return { kind: 'failed' };
    // LXC4: a logout this tab is still sending lands first, so it never logs out the login that follows.
    await this.settling;
    const outcome = await logIn(deps, email, password, await this.highest(deps.store, email));
    if (outcome.kind === 'logged-in') {
      await this.admitted(email, outcome.answer, outcome.highest);
      await this.unlock({ account: outcome.answer.account, email, typed: outcome.typed });
      return { kind: 'done' };
    }
    if (outcome.kind === 'second-factor') {
      // GP3: a login waiting before is replaced; its wrapping key zeroed.
      this.pending?.typed.wrapping.fill(0);
      this.pending = { email, highest: outcome.highest, typed: outcome.typed };
      return { kind: 'second-factor', need: outcome.need };
    }
    return outcome;
  }

  /** LG5, LG9, LG10: a login's second step with `ticket`; the backup codes left when one was used. */
  async secondStep(ticket: string, proof: SecondProof): Promise<{ readonly kind: 'done'; readonly backupCodesLeft: number | null } | Problem> {
    const deps = this.deps();
    const pending = this.pending;
    if (deps === null || pending === null) return { kind: 'failed' };
    const outcome = await secondStep(deps, ticket, proof);
    if (outcome.kind !== 'logged-in') return outcome;
    this.pending = null;
    await this.admitted(pending.email, outcome.answer, pending.highest);
    await this.unlock({ account: outcome.answer.account, email: pending.email, typed: pending.typed });
    return { kind: 'done', backupCodesLeft: outcome.answer.backupCodesLeft };
  }

  /** GP3: the `unlocked` listener told, its failure kept from the login (which stands), the wrapping key zeroed after. */
  private async unlock(event: Unlocked): Promise<void> {
    try {
      await this.options.unlocked?.(event);
    } catch {
      // Its own failures are its own (the invite stays waiting, the keyring opens at the next login).
    } finally {
      event.typed.wrapping.fill(0);
    }
  }

  /**
   * SE7, AC8, C8: the writer logs out: the account and its copy are forgotten, and the relay is told. Fix-next 84, table
   * LXC: the device's own logout, signed by its key (LXC6), so the relay mints it no session again until a login with P
   * (and F); owed in the same write that forgets the account (LXC1), so a page closed or offline before the relay takes
   * it sends it at the next start or return to the page (LXC3).
   */
  async logOut(): Promise<void> {
    this.session = null;
    const status = this.status();
    if (status.kind === 'account') await this.options.loggedOut?.(status.id).catch(() => undefined);
    const account = this.accountId();
    this.kept = null;
    await this.change((record) => ({ ...record, account: null, notNow: true, logoutOwed: account === null ? record.logoutOwed : owing(record.logoutOwed, account) }));
    if (account !== null) await this.settleOwed();
  }

  /** The account this device holds (signed out by the relay or not), or null for none. */
  accountId(): AccountId | null {
    const status = this.status();
    return status.kind === 'account' ? status.id : null;
  }

  /** LXC2–LXC5: the logouts this device owes sent, one at a time; a second call while one runs joins it. */
  private settleOwed(): Promise<void> {
    this.settling ??= this.sendOwed().finally(() => {
      this.settling = null;
    });
    return this.settling;
  }

  private async sendOwed(): Promise<void> {
    const deps = this.deps();
    const store = this.store;
    if (deps === null || store === null || !this.options.online()) return;
    // LXC5: the record read again before each send: a login since (here or in another tab) settled its account, and
    // no logout may follow that login. Each account is tried once a call.
    const tried = new Set<AccountId>();
    for (;;) {
      let owed: readonly AccountId[];
      try {
        owed = (await store.record()).logoutOwed;
      } catch {
        return;
      }
      const account = owed.find((each) => !tried.has(each));
      if (account === undefined) return;
      tried.add(account);
      if ((await logOutDevice(deps, account)) === 'failed') return;
      await this.change((record) => ({ ...record, logoutOwed: settled(record.logoutOwed, account) }));
    }
  }

  /**
   * SE1–SE6: a new session and copy, then the account's view (its standing's purge date). A session the relay ended
   * signs the device out, the copy kept (C8); a failure of the network or the relay leaves everything as it was, and
   * the next try waits {@link REFRESH_PAUSE_MS}.
   */
  async refresh(): Promise<void> {
    const deps = this.deps();
    const status = this.status();
    // C8: a device the relay signed out logs in again; refreshing would only be refused again.
    if (deps === null || status.kind !== 'account' || status.signedOut) return;
    const now = this.options.now();
    if (this.lastFailedRefresh !== null && now - this.lastFailedRefresh < REFRESH_PAUSE_MS) return;
    const outcome = await refreshSession(deps, status.id);
    if (outcome.kind !== 'refreshed') {
      if (endsSession(outcome)) await this.ended(deps.store, status.id, outcome);
      else this.lastFailedRefresh = now;
      return;
    }
    this.lastFailedRefresh = null;
    this.session = { account: status.id, issued: outcome.value.session };
    await this.keepCopy(status.id, outcome.value.trial);
    await this.readView(deps, status.id, outcome.value.session.token);
  }

  /**
   * Part 36c: the account's view read again with the session held (after an action, or when the account page opens):
   * its address, F, deletion and terms. Nothing without a live session.
   */
  async refreshView(): Promise<void> {
    const status = this.status();
    const deps = this.deps();
    if (deps === null || status.kind !== 'account') return;
    const token = await this.token();
    if (token !== null) await this.readView(deps, status.id, token);
  }

  /** SE6, CH5, FD1–FD4: `GET /v1/account` with `token`: the copy, the purge date, the address the relay has, the deletion mark. */
  private async readView(deps: Deps, account: AccountId, token: string): Promise<void> {
    const view = await deps.client.get('/v1/account', readAccountView, token);
    // SE6: a session just issued and refused was ended in between (a revoke, a purge): signed out as SE3.
    if (view.kind === 'refused' && endsSession(view)) await this.bearerRefused(deps, account);
    if (view.kind !== 'ok' || view.value.account !== account) return;
    this.viewSignal[1](view.value);
    await this.keepCopy(account, view.value.trial);
    const email = canonicalEmail(view.value.email);
    const now = this.options.now();
    await this.change((record) => {
      const kept = record.account;
      if (kept?.id !== account) return record;
      // CH5: a change of E applied: the address the relay has, with the highest parameters seen under the old one.
      const address = email ?? kept.email;
      const highest = record.highest[address] ?? record.highest[kept.email];
      return {
        ...record,
        account: { ...kept, purgeAt: view.value.purgeAt, sharesEndAt: view.value.sharesEndAt, email: address },
        highest: highest === undefined ? record.highest : { ...record.highest, [address]: highest },
        deleting: marked(record.deleting, account, view.value.deletion, now),
      };
    });
  }

  /** Part 36c: what `GET /v1/account` last said of the account logged in; null before it was read on this page. */
  readonly view = (): AccountView | null => this.viewSignal[0]();

  /** Part 36c: a live session's token for the account logged in, refreshed first when it has ended (SE1); null when none can be had. */
  async token(): Promise<string | null> {
    const status = this.status();
    if (status.kind !== 'account' || status.signedOut) return null;
    if (!this.live()) await this.refresh();
    const session = this.session;
    // A refresh that failed leaves the old session, which is given only while it lasts (J7: by the time judged at).
    return session?.account === status.id && session.issued.expiresAt > this.judgedAt() ? session.issued.token : null;
  }

  /** Part 36c: `GET path` with the session, read by `read`; a refusal that ends the session signs the device out (SE3). */
  async get<T>(path: string, read: (value: unknown) => T | null): Promise<Reply<T>> {
    return this.withSession((client, token) => client.get(path, read, token));
  }

  /** Part 36c: `POST path` with the session (accepting the terms, TA2), as {@link get}. */
  async post<T>(path: string, body: unknown, read: (value: unknown) => T | null): Promise<Reply<T>> {
    return this.withSession((client, token) => client.post(path, body, read, token));
  }

  /** Part 38d: `POST path` with the session, `bytes` as they are (a snapshot part), as {@link get}. */
  async postBytes<T>(path: string, bytes: Uint8Array<ArrayBuffer>, read: (value: unknown) => T | null): Promise<Reply<T>> {
    return this.withSession((client, token) => client.postBytes(path, bytes, read, token));
  }

  /** Part 38c: POSTs `body` as JSON to `path` with this session, for an answer of at most `most` bytes (an invitee's copy). */
  async postForBytes(path: string, body: unknown, most: number): Promise<Reply<Uint8Array<ArrayBuffer>>> {
    return this.withSession((client, token) => client.postForBytes(path, body, most, token));
  }

  /** Part 38d: the relay as the app reaches it without a session (the owner's share-link routes, LP1), or null with accounts off. */
  publicClient(): RelayClient | null {
    return this.deps()?.client ?? null;
  }

  /**
   * Part 38e (table DA, DA1): this device's public key (its SPKI), the one kept or a new one kept, which names this
   * writer device to a share's notes; null with accounts off, or when the store can't keep one.
   */
  async deviceSpki(): Promise<Uint8Array<ArrayBuffer> | null> {
    const deps = this.deps();
    if (deps === null) return null;
    return (await deps.deviceKey().catch(() => null))?.spki ?? null;
  }

  /** `send` with a live session; a refusal that ends it signs the device out (SE3), or makes a deletion final (FD6). */
  private async withSession<T>(send: (client: RelayClient, token: string) => Promise<Reply<T>>): Promise<Reply<T>> {
    const status = this.status();
    const token = await this.token();
    const deps = this.deps();
    // A token is held only for the account logged in, with a setup and a store.
    if (token === null || deps === null || status.kind !== 'account') return { kind: 'failed' };
    const reply = await send(deps.client, token);
    if (reply.kind === 'refused' && endsSession(reply)) await this.bearerRefused(deps, status.id);
    return reply;
  }

  /**
   * FS3: a session token refused (by any route that takes one) says only that the session is no longer good, never
   * whether the device or the account still is: the session is dropped and the device key asked once, whose answer
   * decides (FS1, FS2: final, signed out or still live); FS4: an answer that doesn't come decides nothing.
   */
  private async bearerRefused(deps: Deps, account: AccountId): Promise<void> {
    // The refused session is the account's (a session is held only for the account logged in).
    this.session = null;
    const outcome = await refreshSession(deps, account);
    if (outcome.kind === 'refreshed') {
      this.session = { account, issued: outcome.value.session };
      await this.keepCopy(account, outcome.value.trial);
      return;
    }
    if (endsSession(outcome)) await this.ended(deps.store, account, outcome);
  }

  /** Part 36c: what the account's actions run on (`actions.ts`), or null with accounts off or no store. */
  /** Part 38c, JL7: whether this build names a relay (accounts on), known before the account has started. */
  readonly configured = (): boolean => this.options.setup !== null;

  flowDeps(): FlowDeps | null {
    return this.deps();
  }

  /** C7: the highest parameters kept for `email`, or null. */
  async highestFor(email: Email): Promise<KdfParameters | null> {
    const store = this.store;
    return store === null ? null : this.highest(store, email);
  }

  /**
   * C13, RQ2: the highest parameters kept for any address on this device, each of the two at its highest; null for none
   * (a reset's link names no address, so its new password is never derived under less than any this device has seen).
   */
  async highestOfAll(): Promise<KdfParameters | null> {
    const store = this.store;
    if (store === null) return null;
    const record = await store.record().catch(() => NO_RECORD);
    const all = Object.values(record.highest);
    return all.length === 0 ? null : { memoryKiB: Math.max(...all.map((p) => p.memoryKiB)), passes: Math.max(...all.map((p) => p.passes)) };
  }

  /** NO2: the writer has seen the account's notices up to `id` (0: none there were). */
  async seeNotice(id: number): Promise<void> {
    const status = this.status();
    if (status.kind !== 'account') return;
    await this.change((record) => {
      const kept = record.account;
      return kept?.id === status.id && (kept.seenNotice === null || kept.seenNotice < id) ? { ...record, account: { ...kept, seenNotice: id } } : record;
    });
  }

  /** NO2: the newest notice seen on this device for the account logged in; null before it first looked (or none can be read). */
  async seenNotice(): Promise<number | null> {
    const status = this.status();
    const store = this.store;
    if (status.kind !== 'account' || store === null) return null;
    const record = await store.record().catch(() => NO_RECORD);
    return record.account?.id === status.id ? record.account.seenNotice : null;
  }

  /**
   * DA1, FD1: this device asked for the account's deletion and the relay said it is due at `dueAt`: kept as seen now, so
   * the next view, or an account gone, finds it.
   */
  async deletionAsked(dueAt: number): Promise<void> {
    const status = this.status();
    if (status.kind !== 'account') return;
    const deletion: PendingDeletion = { requestedAt: this.options.now(), dueAt, device: '' };
    const now = this.options.now();
    await this.change((record) => ({ ...record, deleting: marked(record.deleting, status.id, deletion, now) }));
  }

  /** PD2, DA2: a deletion cancelled from this device: its mark goes at once (FD3). */
  async deletionCancelled(): Promise<void> {
    const status = this.status();
    if (status.kind !== 'account') return;
    await this.change((record) => ({ ...record, deleting: marked(record.deleting, status.id, null, 0) }));
  }

  /**
   * SE3, FD5–FD7: the device-key refresh refused `account`'s device with `refusal` (FS1, FS2; never a session token's
   * word, FS3): a deletion made final (FD6), or signed out (C8).
   */
  private async ended(store: AccountStore, account: AccountId, refusal: Problem): Promise<void> {
    if (refusal.kind === 'refused' && refusal.error === 'account-deleted' && (await this.finalFor(store, account))) return;
    await this.outlived(store, account, refusal);
    await this.signedOut(account);
  }

  /**
   * FM4: after its deletion's due date, any refusal of the device key but `account-deleted` (FS1′) says the account was
   * not deleted at the writer's request: still there (revoked, logged out, expired) or purged unpaid (`unauthenticated`,
   * BK16). The mark goes, so the books are kept.
   */
  private async outlived(store: AccountStore, account: AccountId, refusal: Problem): Promise<void> {
    if (refusal.kind !== 'refused' || !['device-revoked', 'login-required', 'logged-out', 'device-expired', 'unauthenticated'].includes(refusal.error)) return;
    const record = await store.record().catch(() => NO_RECORD);
    const mark = record.deleting[account];
    if (mark === undefined || !this.isDue(mark)) return;
    // Fix-next 84, FM9: a device that logged itself out sees no view, so `logged-out` says only that the account is still
    // there: its deletion may not have run yet (FD10). The mark is kept for the next look until the purge has had its
    // time, then goes as FM4's (the books kept, the safe side).
    if (refusal.error === 'logged-out' && this.options.now() < mark.dueAt + LOGGED_OUT_GRACE_MS) return;
    await this.change((kept) => ({ ...kept, deleting: marked(kept.deleting, account, null, 0) }));
  }

  /** FD6: whether `account`'s kept mark makes an `account-deleted` final (FS1′), and if so the deletion carried out (FD8). */
  private async finalFor(store: AccountStore, account: AccountId): Promise<boolean> {
    const record = await store.record().catch(() => NO_RECORD);
    const mark = record.deleting[account];
    return mark !== undefined && this.isDue(mark) && this.doom(store, account);
  }

  /** FD6: the judged time (C2) and this device's own clock past the date, and a day since this device first saw it. */
  private isDue(mark: DeletionMark): boolean {
    const now = this.options.now();
    return this.judgedAt() >= mark.dueAt && now >= mark.dueAt && now >= mark.seenAt + DAY_MS;
  }

  /**
   * FD8: `account`'s deletion is final: its shelf's storage queued for deletion, the first store's claim released for
   * good when it was the claimer (FC1), the account logged out here and its mark gone, in one transaction; the page then moves (BK8).
   */
  private async doom(store: AccountStore, account: AccountId): Promise<boolean> {
    let record: AccountRecord;
    try {
      const claim = await store.claim();
      const names = this.options.namesOf(claim === account ? { kind: 'first' } : { kind: 'own', account });
      record = await store.doom(account, names, (now) => ({
        ...now,
        account: now.account?.id === account ? null : now.account,
        notNow: now.account?.id === account ? true : now.notNow,
        deleting: marked(now.deleting, account, null, 0),
      }));
    } catch {
      return false;
    }
    if (this.session?.account === account) this.session = null;
    await this.settleClaim(store, record);
    await this.readDoomed(store);
    await this.show(record);
    this.channel?.postMessage(CHANGED);
    return true;
  }

  /**
   * FD7, FD10: for each account with a mark that is due, this device's key asks the relay for a session: `unauthenticated`
   * makes the deletion final (FD8); a session given shows the account still exists, and its view keeps the mark or drops
   * it (FD3). At most once a {@link REFRESH_PAUSE_MS}, and never for the account logged in with a live session (its
   * refresh does it).
   */
  private async checkGone(): Promise<void> {
    const deps = this.deps();
    if (deps === null) return;
    const now = this.options.now();
    if (this.lastGoneCheck !== null && now - this.lastGoneCheck < REFRESH_PAUSE_MS) return;
    const record = await deps.store.record().catch(() => NO_RECORD);
    const status = this.status();
    const live = status.kind === 'account' && !status.signedOut ? status.id : null;
    const due = Object.entries(record.deleting).flatMap(([account, mark]) => (isAccountIdOf(account) && account !== live && this.isDue(mark) ? [account] : []));
    if (due.length === 0) return;
    this.lastGoneCheck = now;
    for (const id of due) {
      const outcome = await refreshSession(deps, id);
      if (outcome.kind === 'refused' && outcome.error === 'account-deleted') {
        await this.finalFor(deps.store, id);
        continue;
      }
      if (outcome.kind !== 'refreshed') {
        await this.outlived(deps.store, id, outcome);
        continue;
      }
      const view = await deps.client.get('/v1/account', readAccountView, outcome.value.session.token);
      if (view.kind !== 'ok' || view.value.account !== id) continue;
      const deletion = view.value.deletion;
      await this.change((kept) => ({ ...kept, deleting: marked(kept.deleting, id, deletion, now) }));
    }
  }

  /** FD9: deletes each queued store; one deleted leaves the queue, one a page holds stays for the next start. */
  private async deleteDoomed(store: AccountStore): Promise<void> {
    const queued = await store.doomed().catch((): readonly DoomedNames[] => []);
    for (const names of queued) {
      const gone = await this.options.deleteStores(names).catch(() => false);
      if (gone) await store.cleared(names.books).catch(() => undefined);
    }
  }

  /** FD9: which book stores are queued and not deleted yet (another tab's deletion, read with the record). */
  private async readDoomed(store: AccountStore): Promise<void> {
    const queued = await store.doomed().catch((): readonly DoomedNames[] => []);
    this.doomedBooks = new Set(queued.map((names) => names.books));
  }

  /** Whether a session is held with more than {@link SESSION_MARGIN_MS} left, by the time judged at (J7), never the raw clock. */
  private live(): boolean {
    return this.session !== null && this.session.issued.expiresAt - this.judgedAt() > SESSION_MARGIN_MS;
  }

  /** The flows' dependencies, or null with accounts off or no store. */
  private deps(): Deps | null {
    const setup = this.options.setup;
    const store = this.store;
    if (setup === null || store === null) return null;
    return {
      client: setup.client,
      subtle: setup.subtle,
      derive: setup.derive,
      random: this.options.random,
      deviceKey: () => this.deviceKey(setup.subtle, store),
      store,
    };
  }

  /** This device's key: the one kept, or a new one kept (another tab's, if it kept one first: CL14). */
  private async deviceKey(subtle: SubtleCrypto, store: AccountStore): Promise<DeviceKey> {
    const kept = await store.deviceKey();
    if (kept !== null) return kept;
    const made = await createDeviceSigningKey(subtle);
    return store.keepDeviceKey({ privateKey: made.privateKey, spki: made.publicKeySpki });
  }

  /** C7: the highest parameters seen for `email`, or null (none when the record can't be read: 25a's floor still holds). */
  private async highest(store: AccountStore, email: Email): Promise<KdfParameters | null> {
    const record = await store.record().catch(() => NO_RECORD);
    return record.highest[email] ?? null;
  }

  /** LG4, SU6: a device admitted: its session held, the account and its highest parameters kept, its copy through C2. */
  private async admitted(email: Email, answer: LoggedIn, highest: KdfParameters): Promise<void> {
    this.session = { account: answer.account, issued: answer.session };
    this.lastFailedRefresh = null;
    const copy = newerCopy(null, await this.verified(answer.trial), answer.account);
    this.kept = copy;
    const fresh = copy === null ? null : this.anchored(answer.account, copy);
    const account = (record: AccountRecord): StoredAccount => ({
      id: answer.account,
      email,
      copy: copy?.signed ?? null,
      signedOut: false,
      purgeAt: null,
      sharesEndAt: null,
      // NO2: a re-login of the same account keeps what it had seen; another hasn't looked yet.
      seenNotice: record.account?.id === answer.account ? record.account.seenNotice : null,
    });
    // LXC5: the login cleared the relay's mark, so a logout of this account still owed here is owed no more.
    await this.change((record) => ({
      ...record,
      account: account(record),
      highest: { ...record.highest, [email]: highest },
      notNow: false,
      latestSeen: fresh ?? record.latestSeen,
      logoutOwed: settled(record.logoutOwed, answer.account),
    }));
  }

  /** C8, SE3: the relay ended this device's session: signed out, the copy kept. */
  private async signedOut(account: AccountId): Promise<void> {
    this.session = null;
    await this.change((record) => (record.account?.id === account ? { ...record, account: { ...record.account, signedOut: true } } : record));
  }

  /** C2: `signed` kept when it verifies, names `account` and is newer than the kept one. */
  private async keepCopy(account: AccountId, signed: SignedCopy): Promise<void> {
    const fresh = await this.verified(signed);
    // CL1, CL3: a copy that doesn't verify, or names another account, changes nothing.
    if (fresh?.copy.account !== account) return;
    const copy = newestOf(this.kept, fresh);
    this.kept = copy;
    // TJ1, TJ3: a copy of the account that just came from the relay anchors the time, newer than the kept one or not.
    const issued = this.anchored(account, copy);
    await this.change((record) => (record.account?.id === account ? { ...record, latestSeen: issued, account: { ...record.account, copy: copy.signed } } : record));
  }

  /**
   * TJ1, TJ3: a copy the relay just sent anchors this page's time, and its issue time becomes the time kept (the relay's
   * clock over a device's that was ahead). Returns that time.
   */
  private anchored(account: AccountId, copy: KeptCopy): number {
    this.anchor = { account, issuedAt: copy.copy.issuedAt, receivedAt: this.options.now(), receivedMono: this.options.monotonic() };
    this.latestSeen = copy.copy.issuedAt;
    return copy.copy.issuedAt;
  }

  /** The copy when it verifies under the build's key; null otherwise or with no key. */
  private async verified(signed: SignedCopy): Promise<KeptCopy | null> {
    const verifier = this.verifier;
    return verifier === null ? null : verifyCopy(verifier.subtle, verifier.key, signed);
  }

  /** C2: the latest time seen, kept: never earlier than before. */
  private async keepTime(): Promise<void> {
    // TJ4: the time judged at (never the raw clock once a copy came), kept only when later than the one kept.
    this.clock[1](this.options.now());
    const now = this.judgedAt();
    if (now <= this.latestSeen || this.store === null) return;
    this.latestSeen = now;
    await this.change((record) => (record.latestSeen >= now ? record : { ...record, latestSeen: now }));
  }

  /** Changes the record, shows the state it gives and tells the other tabs (CL14). A write that fails changes nothing. */
  private async change(next: (record: AccountRecord) => AccountRecord): Promise<void> {
    const store = this.store;
    if (store === null) return;
    let record: AccountRecord;
    try {
      record = await store.change(next);
    } catch {
      return;
    }
    await this.settleClaim(store, record);
    await this.readDoomed(store);
    await this.show(record);
    this.channel?.postMessage(CHANGED);
  }

  /** Reads the record again (the start, another tab's change). */
  private async reread(store: AccountStore): Promise<void> {
    let record: AccountRecord;
    try {
      record = await store.record();
    } catch {
      record = NO_RECORD;
    }
    await this.settleClaim(store, record);
    await this.readDoomed(store);
    await this.show(record);
  }

  /**
   * BK2, BK4: the first store's claimer read, or, with an account logged in and nobody's claim, claimed for it (one
   * transaction: of two tabs, one claims). A claim that can't be kept is read as it stands (deep-fix r8: a device that
   * refuses writes still shows the claimer its books; none kept: BK4's first store); one that can't be read is not
   * known (BK9).
   */
  private async settleClaim(store: AccountStore, record: AccountRecord): Promise<void> {
    try {
      this.claimed = record.account === null ? await store.claim() : await store.claimFor(record.account.id);
    } catch {
      this.claimed = await store.claim().catch(() => undefined);
    }
  }

  private setShelf(shelf: Shelf): void {
    const now = this.shelfSignal[0]();
    if (now === null || !sameShelf(now, shelf)) this.shelfSignal[1](shelf);
    for (const wake of this.waiting.splice(0)) wake(shelf);
  }

  /** The state `record` gives, its copy checked again (C2); the session goes when the account changed (another tab). */
  private async show(record: AccountRecord): Promise<void> {
    const account = record.account;
    const signed = account?.copy ?? null;
    const copy = signed === null ? null : await this.verified(signed);
    const kept = account === null ? null : newerCopy(null, copy, account.id);
    // The session is the account's: gone when the account is (logged out, signed out, another in another tab).
    if (this.session !== null && (account === null || account.signedOut || account.id !== this.session.account)) this.session = null;
    // TJ1: so is the time a copy anchored; part 36c: and the account's view.
    if (this.anchor !== null && account?.id !== this.anchor.account) this.anchor = null;
    if (this.viewSignal[0]()?.account !== account?.id) this.viewSignal[1](null);
    this.kept = kept;
    batch(() => {
      // TJ6: the record's time, which a fresh copy may have lowered (TJ3).
      this.latestSeen = record.latestSeen;
      this.clock[1](this.options.now());
      this.statusSignal[1](
        account === null
          ? { kind: 'none', gate: !record.notNow && !this.dismissed }
          : { kind: 'account', id: account.id, email: account.email, signedOut: account.signedOut, copy: kept?.copy ?? null, purgeAt: account.purgeAt, sharesEndAt: account.sharesEndAt },
      );
      const shelf = shelfOf(account?.id ?? null, this.claimed);
      // FD9: a store queued for deletion is a deleted account's: no page shows it, the empty store in its place.
      this.setShelf(shelf.kind !== 'none' && this.doomedBooks.has(this.options.namesOf(shelf).books) ? { kind: 'none' } : shelf);
    });
  }
}
